import React, { useEffect, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, History, Download, PhoneCall } from "lucide-react";
import { toast } from "sonner";

const YearBadge = ({ y, p }) => (
  <span className="inline-flex items-center rounded-full bg-secondary text-primary px-2 py-0.5 text-xs font-medium">
    {y}: {p != null ? `${p}%` : "—"}
  </span>
);

export default function AdminHistorial() {
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);

  const [pending, setPending] = useState(null);
  const [loadingPending, setLoadingPending] = useState(false);
  const [campaign, setCampaign] = useState(null);

  const doSearch = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 2) { toast.error("Escribe al menos 2 caracteres."); return; }
    setSearching(true); setResult(null);
    try {
      const { data } = await api.get("/admin/beca-history/search", { params: { q: q.trim() } });
      setResult(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSearching(false); }
  };

  const loadPending = async () => {
    setLoadingPending(true);
    try {
      const { data } = await api.get("/admin/beca-history/not-applied");
      setPending(data.persons); setCampaign(data.campaign);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setLoadingPending(false); }
  };
  useEffect(() => { loadPending(); }, []);

  const downloadCsv = async () => {
    const token = localStorage.getItem("eim_token");
    const resp = await fetch(`${API_BASE}/admin/beca-history/not-applied?format=csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await resp.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "becados_sin_solicitar.csv"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout>
      <div className="flex items-center gap-3 mb-2">
        <History className="w-6 h-6 text-accent" />
        <h1 className="text-2xl font-bold">Historial de becas</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Consulta si una persona tuvo beca alguna vez (2016 en adelante), aunque nunca haya usado el sistema, y revisa quiénes tuvieron beca pero aún no postulan este año.
      </p>

      {/* Buscador */}
      <div className="bg-white rounded-2xl border border-border p-6 mb-8">
        <h2 className="font-semibold text-primary mb-3">Buscar por nombre o RUT</h2>
        <form onSubmit={doSearch} className="flex gap-2 max-w-xl">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre, correo o RUT (ej: 12.345.678-5)"
              className="h-11 rounded-xl pl-9" data-testid="historial-search-input" />
          </div>
          <Button type="submit" className="h-11 rounded-xl px-6" disabled={searching} data-testid="historial-search-btn">
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buscar"}
          </Button>
        </form>

        {result && (
          <div className="mt-5" data-testid="historial-results">
            {result.query_type === "rut" && (
              <p className="text-sm mb-3">
                {result.in_eim_base
                  ? <>RUT encontrado en la base EIM: <strong>{result.base_name || "—"}</strong>.</>
                  : <span className="text-amber-700">Ese RUT no está en la base EIM.</span>}
              </p>
            )}
            {result.persons.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="historial-no-results">
                No se encontró historial de becas para esa búsqueda.
              </p>
            ) : (
              <div className="space-y-3">
                {result.persons.map((p) => (
                  <div key={p.name_key} className="border border-border rounded-xl p-4" data-testid={`historial-person-${p.name_key}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <p className="font-semibold text-primary">{p.nombre}</p>
                        {p.email && <p className="text-xs text-muted-foreground">{p.email}</p>}
                      </div>
                      <span className="text-xs text-slate-500">{p.history.length} año(s) con beca · último {p.last_year} ({p.last_pct}%)</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {p.history.map((h, i) => <YearBadge key={i} y={h.year} p={h.percentage} />)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sin solicitar este año */}
      <div className="bg-white rounded-2xl border border-border p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="font-semibold text-primary flex items-center gap-2"><PhoneCall className="w-4 h-4 text-accent" /> Tuvieron beca antes y aún no postulan este año</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Campaña: <strong>{campaign || "—"}</strong>. Cruce por nombre/correo (el histórico no tiene RUT), úsalo como referencia para llamar o invitar.
            </p>
          </div>
          <Button variant="outline" onClick={downloadCsv} className="rounded-xl" disabled={!pending?.length} data-testid="historial-download-csv">
            <Download className="w-4 h-4 mr-2" /> Descargar Excel/CSV
          </Button>
        </div>

        {loadingPending ? (
          <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-3">{pending?.length || 0} persona(s) por contactar.</p>
            <div className="overflow-x-auto max-h-[520px] overflow-y-auto rounded-xl border border-border">
              <table className="w-full text-sm" data-testid="historial-pending-table">
                <thead className="bg-secondary/60 text-left sticky top-0">
                  <tr className="text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-3 font-semibold">Nombre</th>
                    <th className="px-4 py-3 font-semibold">Correo</th>
                    <th className="px-4 py-3 font-semibold">Último año</th>
                    <th className="px-4 py-3 font-semibold">Último %</th>
                    <th className="px-4 py-3 font-semibold">Años con beca</th>
                  </tr>
                </thead>
                <tbody>
                  {(pending || []).map((p) => (
                    <tr key={p.name_key} className="border-t border-border">
                      <td className="px-4 py-2.5 font-medium text-primary">{p.nombre}</td>
                      <td className="px-4 py-2.5 text-slate-600">{p.email || "—"}</td>
                      <td className="px-4 py-2.5">{p.last_year || "—"}</td>
                      <td className="px-4 py-2.5">{p.last_pct != null ? `${p.last_pct}%` : "—"}</td>
                      <td className="px-4 py-2.5 text-slate-500">{p.history.map((h) => h.year).filter(Boolean).join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
